import { useGlobalSnackbar } from '@/contexts/snackContext';
import { Alert, IconButton, Snackbar, SnackbarProps } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLocalization } from '@/hooks/useLocalization';

export interface SnackbarMessage {
  message: string;
  key: string;
}

/**
 * Wrapper for MUI's Snackbar: https://mui.com/material-ui/react-snackbar/
 *
 * Passing in Severity wraps an Alert with
 * the snackbar: https://mui.com/material-ui/react-alert/#variants
 */
const AUTO_HIDE_MS = 5000;

interface GlobalSnackbarProps {
  /**
   * Story snapshots: skip the enter transition and do not auto-hide, so the
   * captured frame does not depend on animation progress or the hide timer.
   */
  settleForSnapshot?: boolean;
}

const GlobalSnackbar = ({ settleForSnapshot = false }: GlobalSnackbarProps) => {
  const { t } = useLocalization();

  const { open, messageInfo, handleExited, handleClose, severity } =
    useGlobalSnackbar();

  if (!messageInfo?.message) {
    return;
  }

  const commonProps: SnackbarProps = {
    open,
    autoHideDuration: settleForSnapshot ? null : AUTO_HIDE_MS,
    // MUI only starts the auto-hide timer when onClose is set, so both
    // variants need it for autoHideDuration to take effect.
    onClose: handleClose,
    slotProps: {
      transition: {
        onExited: handleExited,
        ...(settleForSnapshot ? { timeout: 0 } : {}),
      },
    },
    anchorOrigin: { vertical: 'top' as const, horizontal: 'center' as const },
  };

  if (severity) {
    return (
      <Snackbar
        key={messageInfo.key}
        {...commonProps}
      >
        <Alert
          severity={severity}
          icon={false}
          variant='filled'
          onClose={handleClose}
        >
          {messageInfo.message}
        </Alert>
      </Snackbar>
    );
  }

  return (
    <Snackbar
      key={messageInfo.key}
      message={messageInfo.message}
      action={
        <IconButton
          aria-label={t('common.close')}
          color='inherit'
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      }
      {...commonProps}
    />
  );
};

export default GlobalSnackbar;
